import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import EventService from "../../services/EventService";
import EventShell, {
  EventError,
  EventLoading,
  EventSuccess,
} from "./EventShell";
import { formatEventRange } from "./eventFormat";

/**
 * Self-serve registration management at /events/registration/:token.
 *
 * Deliberately login-free: the opaque token in the URL is the credential, the
 * same pattern the demo registration flow uses. Attendees who arrive here from
 * a confirmation email are typically not KinShield users and should not be
 * asked to create an account to cancel a free seat.
 */
const EventManage = () => {
  const { token } = useParams();

  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [verificationCode, setVerificationCode] = useState("");
  const [codeRequested, setCodeRequested] = useState(false);

  const load = async () => {
    const result = await EventService.getRegistration(token);
    if (result.success) {
      setRegistration(result.registration);
      setForm({
        firstName: result.registration.firstName || "",
        lastName: result.registration.lastName || "",
        phone: result.registration.phone || "",
        organization: result.registration.organization || "",
        partySize: result.registration.partySize || 1,
        smsConsent: !!result.registration.smsConsent,
        marketingConsent: !!result.registration.marketingConsent,
      });
    } else {
      setError(result.message || "We couldn't find that registration.");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const run = async (action, payload, successMessage) => {
    setBusy(true);
    setError("");
    setMessage("");
    const result = await EventService.registrationAction(
      token,
      action,
      payload,
    );
    setBusy(false);

    if (!result.success) {
      setError(result.message || "That didn't work. Please try again.");
      return false;
    }
    setRegistration(result.registration);
    if (successMessage) setMessage(successMessage);
    return true;
  };

  if (loading) {
    return (
      <EventShell narrow>
        <EventLoading label="Loading your registration…" />
      </EventShell>
    );
  }

  if (!registration) {
    return (
      <EventShell narrow>
        <EventError message={error} />
        <Link className="ev-btn ev-btn-secondary" to="/events">
          Browse events
        </Link>
      </EventShell>
    );
  }

  const cancelled = registration.status === "CANCELLED";
  const eventCancelled = registration.eventStatus === "CANCELLED";

  return (
    <EventShell narrow>
      <h1 className="ev-h1">Your registration</h1>
      <p className="ev-lead">
        {registration.eventTitle}
        <br />
        <span className="ev-small">
          {formatEventRange(
            registration.eventStartsAt,
            registration.eventEndsAt,
          )}
        </span>
      </p>

      {error && <EventError message={error} />}
      {message && <EventSuccess message={message} />}

      <div className="ev-badges">
        <span
          className={`ev-badge ${cancelled ? "ev-badge-danger" : "ev-badge-ok"}`}
        >
          {
            {
              CONFIRMED: "Confirmed",
              WAITLISTED: "On the waitlist",
              CANCELLED: "Cancelled",
              ATTENDED: "Attended",
              NO_SHOW: "Did not attend",
            }[registration.status]
          }
        </span>
        {registration.confirmationCode && !cancelled && (
          <span className="ev-badge">Code {registration.confirmationCode}</span>
        )}
      </div>

      {eventCancelled && (
        <div className="ev-alert ev-alert-error" role="alert">
          <strong>
            This event has been cancelled and will not take place.
          </strong>
          {registration.eventCancellationReason && (
            <> {registration.eventCancellationReason}</>
          )}
          <div className="ev-hint" style={{ marginTop: 8 }}>
            You don't need to do anything — please remove it from your calendar.
          </div>
        </div>
      )}

      {registration.joinUrlAvailable && registration.joinUrl && (
        <div className="ev-panel ev-panel-accent">
          <h2 className="ev-h2" style={{ marginTop: 0, border: "none" }}>
            The event is starting
          </h2>
          <a
            className="ev-btn ev-btn-primary"
            href={registration.joinUrl}
            target="_blank"
            rel="noreferrer noopener"
          >
            Join now
          </a>
        </div>
      )}

      {cancelled ? (
        <div className="ev-panel">
          <p>
            This registration has been cancelled. You can sign up again if you
            change your mind.
          </p>
          <Link
            className="ev-btn ev-btn-primary"
            to={`/events/${registration.eventSlug}/register`}
          >
            Register again
          </Link>
        </div>
      ) : (
        <>
          <div className="ev-panel">
            <h2 className="ev-h2" style={{ marginTop: 0, border: "none" }}>
              Your details
            </h2>

            {editing ? (
              <>
                <div className="ev-field-row">
                  <div className="ev-field">
                    <label htmlFor="mg-first">First name</label>
                    <input
                      id="mg-first"
                      type="text"
                      value={form.firstName}
                      onChange={(e) =>
                        setForm({ ...form, firstName: e.target.value })
                      }
                    />
                  </div>
                  <div className="ev-field">
                    <label htmlFor="mg-last">Last name</label>
                    <input
                      id="mg-last"
                      type="text"
                      value={form.lastName}
                      onChange={(e) =>
                        setForm({ ...form, lastName: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="ev-field">
                  <label htmlFor="mg-phone">Phone</label>
                  <input
                    id="mg-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                  />
                </div>
                <div className="ev-field">
                  <label htmlFor="mg-org">Organization</label>
                  <input
                    id="mg-org"
                    type="text"
                    value={form.organization}
                    onChange={(e) =>
                      setForm({ ...form, organization: e.target.value })
                    }
                  />
                </div>
                <div className="ev-field">
                  <label htmlFor="mg-party">How many people</label>
                  <input
                    id="mg-party"
                    type="number"
                    min="1"
                    max="20"
                    value={form.partySize}
                    onChange={(e) =>
                      setForm({ ...form, partySize: e.target.value })
                    }
                  />
                </div>

                <label className="ev-consent">
                  <input
                    type="checkbox"
                    checked={form.smsConsent}
                    onChange={(e) =>
                      setForm({ ...form, smsConsent: e.target.checked })
                    }
                  />
                  <span className="ev-consent-text">
                    Text me a reminder before the event.
                  </span>
                </label>

                <label className="ev-consent">
                  <input
                    type="checkbox"
                    checked={form.marketingConsent}
                    onChange={(e) =>
                      setForm({ ...form, marketingConsent: e.target.checked })
                    }
                  />
                  <span className="ev-consent-text">
                    Send me scam alerts and KinShield updates.
                  </span>
                </label>

                <div className="ev-btn-row">
                  <button
                    type="button"
                    className="ev-btn ev-btn-primary"
                    disabled={busy}
                    onClick={async () => {
                      const ok = await run(
                        "update",
                        { ...form, partySize: Number(form.partySize) || 1 },
                        "Your details were updated.",
                      );
                      if (ok) setEditing(false);
                    }}
                  >
                    Save changes
                  </button>
                  <button
                    type="button"
                    className="ev-btn ev-btn-quiet"
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="ev-facts">
                  <div className="ev-fact">
                    <span className="ev-fact-label">Name</span>
                    <span>
                      {`${registration.firstName || ""} ${registration.lastName || ""}`.trim() ||
                        "—"}
                    </span>
                  </div>
                  <div className="ev-fact">
                    <span className="ev-fact-label">Email</span>
                    <span>{registration.email}</span>
                  </div>
                  <div className="ev-fact">
                    <span className="ev-fact-label">Phone</span>
                    <span>{registration.phone || "—"}</span>
                  </div>
                  <div className="ev-fact">
                    <span className="ev-fact-label">Attending</span>
                    <span>
                      {registration.partySize}{" "}
                      {registration.partySize === 1 ? "person" : "people"}
                    </span>
                  </div>
                </div>
                <div className="ev-btn-row" style={{ marginBottom: 0 }}>
                  <button
                    type="button"
                    className="ev-btn ev-btn-secondary"
                    onClick={() => setEditing(true)}
                  >
                    Edit details
                  </button>
                  <button
                    type="button"
                    className="ev-btn ev-btn-quiet"
                    disabled={busy}
                    onClick={() =>
                      run("resend", {}, "We've resent your confirmation email.")
                    }
                  >
                    Resend my ticket
                  </button>
                </div>
              </>
            )}
          </div>

          {registration.demoSeatEligible && (
            <div className="ev-panel">
              <h2 className="ev-h2" style={{ marginTop: 0, border: "none" }}>
                Your KinShield demo account
              </h2>

              {registration.claimedSeatCode ? (
                <>
                  <p>
                    Your demo account is{" "}
                    <strong>{registration.claimedSeatCode}</strong>.
                  </p>
                  <p className="ev-hint">
                    The sign-in details are announced at the session — we don't
                    email them.
                  </p>
                </>
              ) : registration.emailVerified ? (
                <>
                  <p className="ev-muted">
                    Claim a full KinShield account to use during the session.
                  </p>
                  <button
                    type="button"
                    className="ev-btn ev-btn-primary"
                    disabled={busy}
                    onClick={() =>
                      run("claim-seat", {}, "Your demo account is reserved.")
                    }
                  >
                    Claim my demo account
                  </button>
                </>
              ) : (
                <>
                  <p className="ev-muted">
                    Verify your email address first — this is the one place we
                    need to be sure we're reaching the right person.
                  </p>

                  {codeRequested ? (
                    <>
                      <div className="ev-field">
                        <label htmlFor="mg-code">
                          Enter the 6-digit code we emailed you
                        </label>
                        <input
                          id="mg-code"
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                        />
                      </div>
                      <div className="ev-btn-row" style={{ marginBottom: 0 }}>
                        <button
                          type="button"
                          className="ev-btn ev-btn-primary"
                          disabled={busy || verificationCode.length < 6}
                          onClick={() =>
                            run(
                              "verify",
                              { code: verificationCode },
                              "Email verified.",
                            )
                          }
                        >
                          Verify
                        </button>
                        <button
                          type="button"
                          className="ev-btn ev-btn-quiet"
                          disabled={busy}
                          onClick={() =>
                            run("request-code", {}, "We've sent a new code.")
                          }
                        >
                          Send a new code
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="ev-btn ev-btn-secondary"
                      disabled={busy}
                      onClick={async () => {
                        const ok = await run(
                          "request-code",
                          {},
                          "Check your email for a 6-digit code.",
                        );
                        if (ok) setCodeRequested(true);
                      }}
                    >
                      Email me a verification code
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          <div className="ev-panel">
            <h2 className="ev-h2" style={{ marginTop: 0, border: "none" }}>
              Can't make it?
            </h2>
            <p className="ev-muted">
              Please cancel so we can offer your seat to someone on the
              waitlist.
            </p>
            <button
              type="button"
              className="ev-btn ev-btn-danger"
              disabled={busy}
              onClick={() => {
                if (
                  window.confirm("Cancel your registration for this event?")
                ) {
                  run("cancel", {}, "Your registration has been cancelled.");
                }
              }}
            >
              Cancel my registration
            </button>
          </div>
        </>
      )}

      <Link className="ev-header-link" to={`/events/${registration.eventSlug}`}>
        ← Back to event details
      </Link>
    </EventShell>
  );
};

export default EventManage;
